import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

import { Auth } from 'src/providers/auth/auth';
import { Authenticated } from 'src/behaviors/authenticated';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Events } from 'src/providers/events/events';
import { Me } from 'src/shared/providers/me';
import { RouteGuardReconfigurationService } from 'src/shared/providers/route-guard-reconfiguration.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import validationUtils from 'src/shared/utils/validation-utils';
import { CommonUtils } from 'src/shared/utils/common-utils';

@Component({
    selector: 'page-login',
    templateUrl: 'login.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class LoginPage implements OnInit {

  cameFromJobLink: boolean;
  errorMessage = '';
  isPrivateJob: boolean;
  loaded: boolean;
  loginForm: UntypedFormGroup;
  submitted = false;

  constructor(
    private auth: Auth,
    private authenticated: Authenticated,
    private authModalService: AuthModalService,
    private customNavCtrl: CustomNavController,
    private events: Events,
    private formBuilder: UntypedFormBuilder,
    private me: Me,
    private route: ActivatedRoute,
    private routeGuardReconfiguration: RouteGuardReconfigurationService,
    private storage: TidyStorage,
    private utils: CommonUtils
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, validationUtils.validateEmail]],
      password: ['', Validators.required]
    });
  }

  async ngOnInit() {
    this.loaded = false;
    const token = this.route.snapshot.paramMap.get('token');
    this.cameFromJobLink = this.customNavCtrl.getParam('cameFromJobLink');
    if (this.cameFromJobLink) {
      this.isPrivateJob = this.customNavCtrl.getParam('isPrivateJob');
      const email = this.customNavCtrl.getParam('email');
      this.loginForm.patchValue({ username: email });
    }
    if (token) {
      await this.authorizeWithToken(token);
    }
    this.loaded = true;
  }

  async onSubmit () {
    this.errorMessage = '';
    this.submitted = true;
    if (this.loginForm.valid) {
      await this.closeKeyboard();
      await this.authorize({
        username: this.loginForm.value.username,
        password: this.loginForm.value.password
      });
    }
  }

  private async authorizeWithToken(token: string) {
    try {
      await this.clearPreviousSessionData();
      await this.auth.login({ auth_token: token });
      const me = await this.me.fetchWithoutCache();
      await this.routeGuardReconfiguration.reconfigureGuardsForInvitedUser();
      try {
        const currentLanguage = await this.me.getMetadata(['external.language']);
        const language = currentLanguage?.[0]?.value || 'en';
        localStorage.setItem('language', language);
      } catch(err) {
        localStorage.setItem('language', 'en');
      }
      await this.authModalService.dismissAuthModal();
      await this.authenticated.onAuthenticated(me);
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 401) {
        this.errorMessage = 'This login link is expired or invalid. Please try logging in again.';
        this.utils.showError(this.errorMessage);
      } else {
        this.errorMessage = err?.message || err?.error?.message || 'Failed to log in. Please try again.';
        this.utils.showError(this.errorMessage);
      }
      this.customNavCtrl.navigateForward('dashboard');
    }
  }

  private async clearPreviousSessionData(): Promise<void> {
    if (!localStorage.getItem('hk_tidy_token')) {
      return;
    }
    const uuid = localStorage.getItem('uuid');
    await this.storage.clear();
    if (uuid !== null) {
      localStorage.setItem('uuid', uuid);
    }
  }

  async authorize(credentials) {
    try {
      await this.auth.login(credentials);
      const me = await this.me.fetchWithoutCache();
      // Reconfigure route guards to remove IncompleteRegisterGuard for invited users
      await this.routeGuardReconfiguration.reconfigureGuardsForInvitedUser();
      try {
        const currentLanguage = await this.me.getMetadata(['external.language']);
        const language = currentLanguage?.[0]?.value || 'en';
        localStorage.setItem('language', language);
      } catch(err) {
        localStorage.setItem('language', 'en');
      }
      await this.authenticated.onAuthenticated(me);
    } catch (err) {
      this.unauthorized(err);
    }
  }

  unauthorized(err): void {
    if (err === 'invalid_grant') {
      this.errorMessage = 'The email and password you entered did not match our records. Please double-check and try again.';
      this.utils.showError(this.errorMessage);
    } else {
      this.errorMessage = err?.message || err?.error?.message;
      this.utils.showError(this.errorMessage);
    }
  }

  goToForgotPassword() {
    this.customNavCtrl.navigateForward('forgot-password');
  }

  async closeKeyboard() {
    if (Capacitor.getPlatform() !== 'web') {
      await Keyboard.hide();
    }
  }

  goToSignUp() {
    this.customNavCtrl.navigateForward('create-account');
  }

}
